// Module type definition inline to avoid import issues

export const PERFORMANCE_OPTIMIZATION_MODULE = {
  id: 'performance-optimization',
  title: 'Performance Optimization Strategies',
  description: 'Learn advanced performance optimization techniques for enterprise systems including caching, CDNs, and resource optimization.',
  duration: 45,
  difficulty: 'Advanced',
  content: `# Performance Optimization Strategies

## Introduction

Performance optimization is critical for enterprise applications that need to serve thousands of concurrent users with minimal latency and maximum throughput.

## Key Performance Metrics

### Core Web Vitals
- **Largest Contentful Paint (LCP)**: < 2.5 seconds
- **First Input Delay (FID)**: < 100 milliseconds  
- **Cumulative Layout Shift (CLS)**: < 0.1

### Backend Performance
- **Response Time**: < 200ms for API calls
- **Throughput**: Requests per second capacity
- **Resource Utilization**: CPU, memory, disk I/O

## Caching Strategies

### Multi-Level Caching Architecture

**Browser Cache**: Static assets, API responses
**CDN Cache**: Global content distribution
**Application Cache**: In-memory data storage
**Database Cache**: Query result optimization

### Cache Implementation Patterns

**Cache-Aside Pattern**: Application manages cache directly
**Write-Through**: Data written to cache and database simultaneously
**Write-Behind**: Data written to cache first, database later
**Refresh-Ahead**: Proactive cache refresh before expiration

## Content Delivery Networks (CDN)

### Global Distribution Strategy
CDNs reduce latency by serving content from geographically distributed edge servers.

**Edge Locations**: Servers closest to users
**Origin Shield**: Additional caching layer
**Anycast Routing**: Automatic traffic routing

### CDN Optimization Techniques
- **Static Asset Optimization**: Images, CSS, JavaScript
- **Dynamic Content Caching**: API responses, HTML
- **Edge Computing**: Server-side logic at edge locations

## Database Performance Optimization

### Query Optimization
- **Index Strategy**: Proper indexing for query patterns
- **Query Analysis**: Execution plan optimization
- **Connection Pooling**: Efficient database connections

### Database Scaling Patterns
- **Read Replicas**: Distribute read operations
- **Sharding**: Horizontal data partitioning
- **CQRS**: Command Query Responsibility Segregation

## Application Performance Optimization

### Code-Level Optimizations
- **Lazy Loading**: Load resources on demand
- **Bundle Splitting**: Reduce initial load size
- **Tree Shaking**: Remove unused code

### Runtime Optimizations
- **Memory Management**: Efficient memory usage
- **CPU Optimization**: Reduce computational overhead
- **I/O Optimization**: Minimize blocking operations

## Monitoring and Observability

### Performance Monitoring Tools
- **Application Performance Monitoring (APM)**
- **Real User Monitoring (RUM)**
- **Synthetic Monitoring**

### Key Performance Indicators
- **Apdex Score**: Application performance index
- **Error Rate**: Percentage of failed requests
- **Throughput**: Requests handled per time unit
- **Resource Utilization**: System resource consumption

## Load Testing and Capacity Planning

### Load Testing Types
**Smoke Testing**: Basic functionality verification
**Load Testing**: Normal expected load
**Stress Testing**: Beyond normal capacity
**Spike Testing**: Sudden load increases

### Capacity Planning
- **Traffic Forecasting**: Predict future load
- **Resource Scaling**: Plan infrastructure growth
- **Performance Budgets**: Set performance constraints`,

  codeExamples: [
    {
      title: "Redis Caching Implementation",
      language: "javascript",
      code: `// Redis cache service implementation
class CacheService {
  constructor(redisClient) {
    this.redis = redisClient;
    this.defaultTTL = 3600; // 1 hour
  }

  async get(key, fallback = null) {
    try {
      const cached = await this.redis.get(key);
      if (cached !== null) {
        return JSON.parse(cached);
      }
      
      if (fallback && typeof fallback === 'function') {
        const result = await fallback();
        await this.set(key, result);
        return result;
      }
      
      return null;
    } catch (error) {
      console.error('Cache get error:', error);
      return fallback ? await fallback() : null;
    }
  }

  async set(key, value, ttl = this.defaultTTL) {
    try {
      await this.redis.setex(key, ttl, JSON.stringify(value));
      return true;
    } catch (error) {
      console.error('Cache set error:', error);
      return false;
    }
  }

  async invalidate(pattern) {
    try {
      const keys = await this.redis.keys(pattern);
      if (keys.length > 0) {
        await this.redis.del(...keys);
      }
      return keys.length;
    } catch (error) {
      console.error('Cache invalidation error:', error);
      return 0;
    }
  }
}`
    },
    {
      title: "Database Query Optimization",
      language: "sql",
      code: `-- Optimized user query with proper indexing
-- Create composite index for common query patterns
CREATE INDEX idx_users_active_created ON users(is_active, created_at DESC)
WHERE is_active = true;

-- Optimized query using the index
SELECT u.id, u.username, u.email, p.display_name
FROM users u
LEFT JOIN profiles p ON u.id = p.user_id
WHERE u.is_active = true
  AND u.created_at >= '2024-01-01'
ORDER BY u.created_at DESC
LIMIT 20;

-- Query plan analysis
EXPLAIN (ANALYZE, BUFFERS) 
SELECT * FROM users 
WHERE is_active = true 
ORDER BY created_at DESC 
LIMIT 20;`
    },
    {
      title: "Performance Monitoring Hook",
      language: "typescript",
      code: `// React performance monitoring hook
import { useEffect, useCallback } from 'react';

interface PerformanceMetrics {
  componentName: string;
  renderTime: number;
  memoryUsage: number;
  timestamp: number;
}

export const usePerformanceMonitor = (componentName: string) => {
  const startTime = performance.now();

  const measurePerformance = useCallback(() => {
    const endTime = performance.now();
    const renderTime = endTime - startTime;
    
    const memoryUsage = (performance as any).memory?.usedJSHeapSize || 0;
    
    const metrics: PerformanceMetrics = {
      componentName,
      renderTime,
      memoryUsage,
      timestamp: Date.now()
    };

    // Send metrics to monitoring service
    if (renderTime > 16) { // > 1 frame at 60fps
      console.warn('Slow render detected:', metrics);
      // sendToMonitoringService(metrics);
    }

    return metrics;
  }, [componentName, startTime]);

  useEffect(() => {
    return () => {
      measurePerformance();
    };
  }, [measurePerformance]);

  return { measurePerformance };
};`
    }
  ],

  practice: {
    title: "Optimize Application Performance",
    description: "Implement performance optimizations for a slow-loading dashboard component.",
    starter: `import React, { useState, useEffect } from 'react';

// Slow dashboard component that needs optimization
const Dashboard = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate slow data loading
    fetchDashboardData().then(result => {
      setData(result);
      setLoading(false);
    });
  }, []);

  const fetchDashboardData = async () => {
    // Simulating multiple API calls
    const users = await fetch('/api/users').then(r => r.json());
    const orders = await fetch('/api/orders').then(r => r.json());
    const analytics = await fetch('/api/analytics').then(r => r.json());
    
    return { users, orders, analytics };
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      <h1>Dashboard</h1>
      {/* Render large lists without optimization */}
      {data.users?.map(user => (
        <UserCard key={user.id} user={user} />
      ))}
      {data.orders?.map(order => (
        <OrderCard key={order.id} order={order} />
      ))}
    </div>
  );
};`,
    solution: `import React, { useState, useEffect, Suspense, lazy, useMemo } from 'react';
import { usePerformanceMonitor } from '../hooks/usePerformanceMonitor';

// Lazy load heavy components
const UserCard = lazy(() => import('./UserCard'));
const OrderCard = lazy(() => import('./OrderCard'));

const Dashboard = () => {
  const [data, setData] = useState({ users: [], orders: [], analytics: null });
  const [loading, setLoading] = useState(true);
  const { measurePerformance } = usePerformanceMonitor('Dashboard');

  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      try {
        // Parallel data loading with caching
        const [users, orders, analytics] = await Promise.all([
          fetchWithCache('/api/users'),
          fetchWithCache('/api/orders'), 
          fetchWithCache('/api/analytics')
        ]);

        if (isMounted) {
          setData({ users, orders, analytics });
          setLoading(false);
          measurePerformance();
        }
      } catch (error) {
        console.error('Dashboard data loading failed:', error);
        setLoading(false);
      }
    };

    loadData();
    
    return () => { isMounted = false; };
  }, [measurePerformance]);

  // Memoize filtered data to prevent unnecessary recalculations
  const filteredUsers = useMemo(() => 
    data.users.filter(user => user.isActive), [data.users]
  );

  const filteredOrders = useMemo(() => 
    data.orders.filter(order => order.status !== 'cancelled'), [data.orders]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Dashboard</h1>
      
      <Suspense fallback={<div>Loading users...</div>}>
        <div className="grid gap-4">
          {filteredUsers.slice(0, 10).map(user => (
            <UserCard key={user.id} user={user} />
          ))}
        </div>
      </Suspense>

      <Suspense fallback={<div>Loading orders...</div>}>
        <div className="grid gap-4">
          {filteredOrders.slice(0, 10).map(order => (
            <OrderCard key={order.id} order={order} />
          ))}
        </div>
      </Suspense>
    </div>
  );
};

// Cache implementation
const cache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const fetchWithCache = async (url: string) => {
  const cacheKey = url;
  const cached = cache.get(cacheKey);
  
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }
  
  const response = await fetch(url);
  const data = await response.json();
  
  cache.set(cacheKey, {
    data,
    timestamp: Date.now()
  });
  
  return data;
};`,
    hints: [
      "Use React.lazy for code splitting heavy components",
      "Implement parallel data loading with Promise.all",
      "Add caching layer to prevent redundant API calls",
      "Use useMemo for expensive filtering operations",
      "Add proper loading states and error boundaries",
      "Implement performance monitoring to track improvements"
    ]
  },

  quickCheck: {
    question: "Which caching strategy writes data to both cache and database simultaneously?",
    options: [
      "Cache-Aside",
      "Write-Through", 
      "Write-Behind",
      "Refresh-Ahead"
    ],
    correctAnswer: 1,
    explanation: "Write-Through caching writes data to both the cache and the database simultaneously, ensuring data consistency but potentially impacting write performance."
  }
};